import React, { useContext, useEffect, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Input, Paper, TextField, Checkbox, FormControlLabel, Button, Container } from '@material-ui/core';
import { createPDS, CreatePDSResponse } from '../services/platform';
import { AxiosResponse } from 'axios';
import { DateTime } from 'luxon';
import { InitialMeasurement, WeightData } from '../types/weightData';
import { getIssuer, setInitialMeasurement, setWeights } from '../services/pda';
import { AuthContext } from './Context/AuthContext';
import { getApplication, HatApplicationContent } from '../services/dex';
import { postStoreData, recordMerchantEvent } from '../services/c2dcrm';
import { config } from '../configs/config';
import { EventComponent, EventSlug } from '../constants/events';
import {v4 as uuidv4} from 'uuid';

const applicationId = config.applicationId;

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    placeItems: 'center',
    width: '90%',
    padding: '10px',
    marginTop: '5px',
    justifyContent: 'center',
    alignItems: 'center',

    [theme.breakpoints.up('sm')]: {
      width: '100%',
    },

  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    maxWidth: '80%'
  },
  item: {
    flex: 1,
    margin: '20px'
  }
}));

export const CustomerSignupCard: React.FC = () => {
  const { user } = useContext(AuthContext);

  const classes = useStyles();
  const [email, setEmail] = useState("");
  const [weight, setWeight] = useState(0.0);
  const [height, setHeight] = useState(0.0);
  const [agreeToHMI, setAgreeToHMI] = useState(false);
  const [buttonState, setButtonState] = useState("Sign Up this client");
  const [token, setToken] = useState('');
  const [app, setApp] = useState<HatApplicationContent | null>(null);

  useEffect(() => {
    getApplication(applicationId).then((app) => {
      setApp(app.data);
    })
  }, []);

  function calculateBMI(height: number, weight: number): number {
    const h = height / 100;
    const bb: number = (weight / (h * h));
    const b = bb.toPrecision(3);
    const bmi = parseFloat(b);

    return (bmi);
  }

  const extractToken = (url: string) => {
    const urlParams = new URLSearchParams(url);
    const paramValue = urlParams.get('token');
    return paramValue;
  }

  const signClientUp = async (email: string): Promise<AxiosResponse<CreatePDSResponse>> => {
    return createPDS(email, "password");
  }

  enum WeightVerdictState {
    Pending = 1,
    Verified,
    Rejected,
  }

  const signUpClient = () => {
    setButtonState("Signing up... please wait");
    if (agreeToHMI) {
      signClientUp(email).then((response) => {
        const t = extractToken(response.data.nextStep);
        if (t) {
          setToken(t);

          const issuer = getIssuer(t);

          if (issuer) {
            const signupBody = {
              weight: weight,
              height: height,
              agreeToHMI: true,
              verifier: user.token,
              signupDate: DateTime.now(),
              state: WeightVerdictState.Verified
            }

            const storeBody = {
              "pda_url": issuer,
              "payload": signupBody
            }

            postStoreData(user.token, "sejutakgfacilitatedsignup", JSON.stringify(storeBody)).then((storeResponse) => {
              console.log("we good1.");
              if (storeResponse.status === 201) {
                recordMerchantEvent(user.token, EventComponent.MDT_SIGNUP, uuidv4(), EventSlug.MDT_SIGNUP).then((eventResponse) => {
                  console.log("we good2.");
                  console.log(eventResponse);
                  setButtonState(`Ok, it's done, tell the user to watch for an email sent to ${email}`);  
                })
              }
              else if (response.status >= 400) {
                console.log("bad response");
                setButtonState(`Looks like there was an error creating the PDA.`);
              }
              else {
                console.log("bad response");
                setButtonState(`Looks like there was an unknown error creating the PDA.`);
              }
            })
          }
        }


      })
    }
    else {
      setButtonState("Did the user agree to the terms?");
    }
  }

  return (
    <Container maxWidth="sm">
      <Paper className={classes.container}>
        <div style={{ flex: 1 }}>
          <Typography variant="subtitle1">Enter the weight, height and email of the new client</Typography>
        </div>
        <div className={classes.content}>
          <form noValidate autoComplete="off">
            <div className={classes.item}>
              <TextField
                id="emailField"
                label="Email"
                variant="outlined"
                onChange={(k) => setEmail(k.target.value)}
              />
            </div>

            <div className={classes.item}>
              <TextField
                id="heightField"
                label="Height in cm"
                variant="outlined"
                onChange={(k) => setHeight(parseFloat(k.target.value))}
              />
            </div>

            <div className={classes.item}>
              <TextField
                id="weightField"
                label="Weight in kg"
                variant="outlined"
                onChange={(k) => setWeight(parseFloat(k.target.value))}
              />
            </div>

            <div className={classes.item}>
              <Typography>Please read the following terms for <br />{app?.info.name} from developer {app?.developer.name}:<br /></Typography>
              <Typography>{app?.info.name} has a rating of <strong>{app?.info.rating.score}</strong></Typography>
              {/* {app?.info.rating.score && app?.info.rating.points &&
              <CertificateStatus
                score={app?.info.rating.score}
                points={app?.info.rating.points}
                learnMoreSection={false}
              />
            } */}
              <Typography><strong>Read and Write Access:</strong><br />{app?.info.name} will be able to read and write from/to your personal data account's <strong>{app?.permissions.rolesGranted[0].detail}</strong> folder.</Typography>
              <Typography><strong>Purpose of Access:</strong><br />{app?.info.dataUsePurpose}</Typography>
              <Typography><strong>Duration of Access:</strong><br />{app?.info.name} will have access until disabled. (Please refer to your PDA dashboard.)</Typography>
              <Typography>HMI ID: {app?.id}-{app?.info.version}</Typography>
            </div>

            <div className={classes.item}>
              <FormControlLabel
                control={<Checkbox
                  checked={agreeToHMI}
                  onChange={(k) => setAgreeToHMI(k.target.checked)}
                  name="agreeToHMI" />}
                label="Do you agree with the above terms?"
              />
            </div>

            <div className={classes.item}>
              <Button variant="outlined"
                onClick={() => signUpClient()}
              >
                {buttonState}
              </Button>
            </div>
          </form>
        </div>
      </Paper>
    </Container>
  );
};


